import { FormType, type LabelType } from '@piccolohealth/pbs-common';
import { ChakraV3, type SelectOption, Spin } from '@piccolohealth/ui';
import React from 'react';
import { FormSection } from '../../../components/forms/FormSection';
import { FormStack } from '../../../components/forms/FormStack';
import { HookedFormField } from '../../../components/forms/HookedFormField';
import { HookedInput } from '../../../components/forms/HookedInput';
import { HookedMultiSelect } from '../../../components/forms/HookedMultiSelect';
import { HookedSelect } from '../../../components/forms/HookedSelect';
import { PiccoloError } from '../../../components/generic/PiccoloError';
import { useLabelsQuery } from '../../../graphql/hooks/useLabelsQuery';
import { useAppContext } from '../../../hooks/useAppContext';
import { formTypeColorName } from './FormTypeTag';

interface Props {
  type: FormType;
}

const formTypeOptions: SelectOption<FormType>[] = Object.values(FormType).map((type) => {
  const [color, label] = formTypeColorName(type);
  return {
    label,
    color,
    value: type,
    raw: type,
  };
});

export const FormForm = (props: Props) => {
  const { type } = props;

  const { organization } = useAppContext();

  const { data, isLoading, error } = useLabelsQuery({
    organizationId: organization.id,
    type: [type as unknown as LabelType],
  });

  const labels = data?.organization?.labels ?? [];

  const labelOptions = labels.map((label) => {
    return {
      label: label.name,
      value: label.id,
      raw: label,
      color: label.color,
    };
  });

  if (isLoading) {
    return <Spin />;
  }

  if (error) {
    return <PiccoloError error={error} />;
  }

  return (
    <FormStack gap={4}>
      <FormSection heading='Basic' description='Basic information about the form'>
        <HookedFormField name='name' label='Name'>
          <HookedInput name='name' size='sm' placeholder='Enter a name' />
        </HookedFormField>

        <HookedFormField name='url' label='URL'>
          <HookedInput name='url' size='sm' placeholder='Enter a url' />
        </HookedFormField>

        <HookedFormField name='externalId' label='External ID'>
          <HookedInput name='externalId' size='sm' placeholder='Enter an external ID' />
        </HookedFormField>

        <HookedFormField name='type' label='Type'>
          <HookedSelect
            name='type'
            size='sm'
            placeholder='Choose the type of form this relates to'
            options={formTypeOptions}
            optionVariant='tag'
          />
        </HookedFormField>
      </FormSection>

      <ChakraV3.Separator />

      <FormSection
        heading='Labels'
        description='What labels should be automatically attached for the following events?'
      >
        <HookedFormField
          name='labelMappings.create'
          label='Create event'
          helperText='Labels to attach when a form submission is received and triggers creation'
        >
          <HookedMultiSelect
            name='labelMappings.create'
            options={labelOptions}
            size='sm'
            placeholder='Select labels to add after creation'
            optionVariant='tag'
          />
        </HookedFormField>

        <HookedFormField
          name='labelMappings.update'
          label='Update event'
          helperText='Labels to attach when a form submission is received and triggers an update'
        >
          <HookedMultiSelect
            name='labelMappings.update'
            options={labelOptions}
            size='sm'
            placeholder='Select labels to add after updating'
            optionVariant='tag'
          />
        </HookedFormField>
      </FormSection>
    </FormStack>
  );
};
